import { _basePortalApi } from "./base";
import {
  Autotractor,
  Implement,
  ImplementArchetype,
  JobSettings,
  Tractor,
  TractorArchetype,
} from "protos/atk/autotractor";
import {
  CheckRigCompatibilityResponse,
  ConfigValidationResult,
  GetRigResponse,
  ListCompatibleRobotsResponse,
  SearchImplementArchetypesRequest,
  SearchImplementArchetypesResponse,
  SearchImplementsRequest,
  SearchImplementsResponse,
  SearchRigsRequest,
  SearchRigsResponse,
  SearchTractorArchetypesRequest,
  SearchTractorArchetypesResponse,
  SearchTractorsRequest,
  SearchTractorsResponse,
} from "protos/portal/atk_equipment";
import { EffectiveConfigValidationMode } from "protos/atk/effective_config";
import { Empty } from "protos/google/protobuf/empty";

enum EquipmentApiTag {
  TRACTOR = "Tractor",
  TRACTOR_ARCHETYPE = "TractorArchetype",
  TRACTOR_SEARCH = "TractorSearch",
  TRACTOR_ARCHETYPE_SEARCH = "TractorArchetypeSearch",
  IMPLEMENT = "Implement",
  IMPLEMENT_ARCHETYPE = "ImplementArchetype",
  IMPLEMENT_SEARCH = "ImplementSearch",
  IMPLEMENT_ARCHETYPE_SEARCH = "ImplementArchetypeSearch",
  RIG = "Rig",
  RIG_SEARCH = "RigSearch",
  RIG_LIST = "RigList",
  RIG_EFFECTIVE_CONFIG = "RigEffectiveConfig",
}

const portalWithTags = _basePortalApi.enhanceEndpoints({
  addTagTypes: Object.values(EquipmentApiTag),
});

export const atkEquipmentApi = portalWithTags.injectEndpoints({
  endpoints: (builder) => ({
    searchRigs: builder.query<SearchRigsResponse, SearchRigsRequest>({
      query: (request) => ({
        url: `atk-equipment/rigs/search`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        SearchRigsResponse.fromJSON(response),
      providesTags: (response) =>
        response?.rigs
          ? [
              ...response.rigs.map((i) => ({
                id: i.id,
                type: EquipmentApiTag.RIG,
              })),
              EquipmentApiTag.RIG_LIST,
            ]
          : [EquipmentApiTag.RIG_LIST],
    }),
    getRig: builder.query<
      GetRigResponse,
      Pick<Autotractor, "id"> & {
        validate?: boolean;
        validationMode?: EffectiveConfigValidationMode;
      }
    >({
      query: ({ id, validate }) => ({
        url: `atk-equipment/rigs/${id}`,
        params: { validate },
        method: "GET",
      }),
      transformResponse: (response: unknown) =>
        GetRigResponse.fromJSON(response),
      providesTags: (response) =>
        response?.rig
          ? [{ id: response.rig.id, type: EquipmentApiTag.RIG }]
          : [],
    }),
    deleteRig: builder.mutation<Empty, Pick<Autotractor, "id">>({
      query: ({ id }) => ({
        url: `atk-equipment/rigs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, _err, { id }) => [
        EquipmentApiTag.RIG_LIST,
        EquipmentApiTag.RIG_SEARCH,
        { id, type: EquipmentApiTag.RIG },
        { id, type: EquipmentApiTag.RIG_EFFECTIVE_CONFIG },
        { id, type: EquipmentApiTag.RIG_SEARCH },
      ],
    }),
    prevalidateRig: builder.query<
      ConfigValidationResult,
      { rig: Autotractor; validationMode?: EffectiveConfigValidationMode }
    >({
      query: ({ rig, validationMode }) => ({
        url: "atk-equipment/rigs/prevalidate",
        method: "POST",
        body: Autotractor.toJSON(rig),
        params: {
          validationMode,
        },
      }),
      transformResponse: (response: unknown) =>
        ConfigValidationResult.fromJSON(response),
    }),
    searchImplements: builder.query<
      SearchImplementsResponse,
      SearchImplementsRequest
    >({
      query: (request) => ({
        url: `atk-equipment/implements/search`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        SearchImplementsResponse.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.IMPLEMENT_SEARCH,
              ...response.implements.map((i) => ({
                id: i.id,
                type: EquipmentApiTag.IMPLEMENT,
              })),
            ]
          : [EquipmentApiTag.IMPLEMENT_SEARCH],
    }),
    searchTractors: builder.query<
      SearchTractorsResponse,
      SearchTractorsRequest
    >({
      query: (request) => ({
        url: `atk-equipment/tractors/search`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        SearchTractorsResponse.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.TRACTOR_SEARCH,
              ...response.tractors.map((t) => ({
                id: t.id,
                type: EquipmentApiTag.TRACTOR,
              })),
            ]
          : [EquipmentApiTag.TRACTOR_SEARCH],
    }),
    getImplementArchetype: builder.query<
      ImplementArchetype,
      Pick<ImplementArchetype, "id">
    >({
      query: ({ id }) => ({
        url: `atk-equipment/implement-archetypes/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown) =>
        ImplementArchetype.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH,
              {
                id: response.id,
                type: EquipmentApiTag.IMPLEMENT_ARCHETYPE,
              },
            ]
          : [EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH],
    }),
    getTractorArchetype: builder.query<
      TractorArchetype,
      Pick<TractorArchetype, "id">
    >({
      query: ({ id }) => ({
        url: `atk-equipment/tractor-archetypes/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown) =>
        TractorArchetype.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH,
              {
                id: response.id,
                type: EquipmentApiTag.TRACTOR_ARCHETYPE,
              },
            ]
          : [EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH],
    }),
    searchImplementArchetypes: builder.query<
      SearchImplementArchetypesResponse,
      SearchImplementArchetypesRequest
    >({
      query: (request) => ({
        url: `atk-equipment/implement-archetypes/search`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        SearchImplementArchetypesResponse.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH,
              ...response.archetypes.map((t) => ({
                id: t.id,
                type: EquipmentApiTag.IMPLEMENT_ARCHETYPE,
              })),
            ]
          : [EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH],
    }),
    searchInfiniteImplementArchetypes: builder.infiniteQuery<
      SearchImplementArchetypesResponse,
      SearchImplementArchetypesRequest,
      SearchImplementArchetypesRequest["pageToken"]
    >({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.nextPageToken,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `atk-equipment/implement-archetypes/search`,
        method: "POST",
        body: {
          ...queryArg,
          pageSize: queryArg.pageSize ?? 100,
          pageToken: pageParam,
        },
      }),
      transformResponse: (response: unknown) =>
        SearchImplementArchetypesResponse.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH,
              ...response.pages.flatMap((page) =>
                page.archetypes.map((t) => ({
                  id: t.id,
                  type: EquipmentApiTag.IMPLEMENT_ARCHETYPE,
                }))
              ),
            ]
          : [EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH],
    }),
    searchTractorArchetypes: builder.query<
      SearchTractorArchetypesResponse,
      SearchTractorArchetypesRequest
    >({
      query: (request) => ({
        url: `atk-equipment/tractor-archetypes/search`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        SearchTractorArchetypesResponse.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH,
              ...response.archetypes.map((t) => ({
                id: t.id,
                type: EquipmentApiTag.TRACTOR_ARCHETYPE,
              })),
            ]
          : [EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH],
    }),
    searchInfiniteTractorArchetypes: builder.infiniteQuery<
      SearchTractorArchetypesResponse,
      SearchTractorArchetypesRequest,
      SearchTractorArchetypesRequest["pageToken"]
    >({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.nextPageToken,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `atk-equipment/tractor-archetypes/search`,
        method: "POST",
        body: {
          ...queryArg,
          pageSize: queryArg.pageSize ?? 100,
          pageToken: pageParam,
        },
      }),
      transformResponse: (response: unknown) =>
        SearchTractorArchetypesResponse.fromJSON(response),
      providesTags: (response) =>
        response
          ? [
              EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH,
              ...response.pages.flatMap((page) =>
                page.archetypes.map((t) => ({
                  id: t.id,
                  type: EquipmentApiTag.TRACTOR_ARCHETYPE,
                }))
              ),
            ]
          : [EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH],
    }),
    createImplementArchetype: builder.mutation<
      ImplementArchetype,
      Omit<ImplementArchetype, "id" | "updatedAt">
    >({
      query: (request) => ({
        url: `atk-equipment/implement-archetypes`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        ImplementArchetype.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH,
              { id: resp.id, type: EquipmentApiTag.IMPLEMENT_ARCHETYPE },
            ]
          : [EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH],
    }),
    createImplement: builder.mutation<
      Implement,
      Omit<Implement, "id" | "updatedAt" | "customerId"> & {
        customerId: string | undefined;
      }
    >({
      query: (request) => ({
        url: `atk-equipment/implements`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) => Implement.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.IMPLEMENT_SEARCH,
              { id: resp.id, type: EquipmentApiTag.IMPLEMENT },
            ]
          : [EquipmentApiTag.IMPLEMENT_SEARCH],
    }),
    updateImplement: builder.mutation<Implement, Partial<Implement>>({
      query: (request) => ({
        url: `atk-equipment/implements/${request.id}`,
        method: "PUT",
        body: request,
      }),
      transformResponse: (response: unknown) => Implement.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.IMPLEMENT_SEARCH,
              { id: resp.id, type: EquipmentApiTag.IMPLEMENT },
            ]
          : [EquipmentApiTag.IMPLEMENT_SEARCH],
    }),
    updateImplementArchetype: builder.mutation<
      ImplementArchetype,
      Partial<ImplementArchetype>
    >({
      query: (request) => ({
        url: `atk-equipment/implement-archetypes/${request.id}`,
        method: "PUT",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        ImplementArchetype.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH,
              { id: resp.id, type: EquipmentApiTag.IMPLEMENT_ARCHETYPE },
            ]
          : [EquipmentApiTag.IMPLEMENT_ARCHETYPE_SEARCH],
    }),
    updateTractor: builder.mutation<Tractor, Partial<Tractor>>({
      query: (request) => ({
        url: `atk-equipment/tractors/${request.id}`,
        method: "PUT",
        body: request,
      }),
      transformResponse: (response: unknown) => Tractor.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.TRACTOR_SEARCH,
              { id: resp.id, type: EquipmentApiTag.TRACTOR },
            ]
          : [EquipmentApiTag.TRACTOR_SEARCH],
    }),
    updateTractorArchetype: builder.mutation<
      TractorArchetype,
      Partial<TractorArchetype>
    >({
      query: (request) => ({
        url: `atk-equipment/tractor-archetypes/${request.id}`,
        method: "PUT",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        TractorArchetype.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH,
              { id: resp.id, type: EquipmentApiTag.TRACTOR_ARCHETYPE },
            ]
          : [EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH],
    }),
    deleteTractor: builder.mutation<Empty, Pick<Tractor, "id">>({
      query: ({ id }) => ({
        url: `atk-equipment/tractors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, _err, { id }) => [
        EquipmentApiTag.TRACTOR_SEARCH,
        { id, type: EquipmentApiTag.TRACTOR },
      ],
    }),
    deleteImplement: builder.mutation<Empty, Pick<Implement, "id">>({
      query: ({ id }) => ({
        url: `atk-equipment/implements/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, _err, { id }) => [
        EquipmentApiTag.IMPLEMENT_SEARCH,
        { id, type: EquipmentApiTag.IMPLEMENT },
      ],
    }),
    createTractorArchetype: builder.mutation<
      TractorArchetype,
      Omit<TractorArchetype, "id" | "updatedAt">
    >({
      query: (request) => ({
        url: `atk-equipment/tractor-archetypes`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) =>
        TractorArchetype.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH,
              { id: resp.id, type: EquipmentApiTag.TRACTOR_ARCHETYPE },
            ]
          : [EquipmentApiTag.TRACTOR_ARCHETYPE_SEARCH],
    }),
    createTractor: builder.mutation<
      Tractor,
      Omit<Tractor, "id" | "updatedAt" | "customerId"> & {
        customerId: string | undefined;
      }
    >({
      query: (request) => ({
        url: `atk-equipment/tractors`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: unknown) => Tractor.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              EquipmentApiTag.TRACTOR_SEARCH,
              { id: resp.id, type: EquipmentApiTag.TRACTOR },
            ]
          : [EquipmentApiTag.TRACTOR_SEARCH],
    }),
    createRig: builder.mutation<
      Autotractor,
      Omit<Autotractor, "id" | "updatedAt" | "customerId"> & {
        customerId: string | undefined;
      }
    >({
      query: (request) => ({
        url: `atk-equipment/rigs`,
        method: "POST",
        body: {
          ...request,
          jobSettings: request.jobSettings
            ? JobSettings.toJSON(request.jobSettings)
            : undefined,
        },
      }),
      transformResponse: (response: unknown) => Autotractor.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              { id: resp.id, type: EquipmentApiTag.RIG },
              EquipmentApiTag.RIG_LIST,
            ]
          : [EquipmentApiTag.RIG_LIST],
    }),
    updateRig: builder.mutation<
      Autotractor,
      Omit<Autotractor, "updatedAt" | "customerId">
    >({
      query: (request) => ({
        url: `atk-equipment/rigs/${request.id}`,
        method: "PUT",
        body: {
          ...request,
          jobSettings: request.jobSettings
            ? JobSettings.toJSON(request.jobSettings)
            : undefined,
        },
      }),
      transformResponse: (response: unknown) => Autotractor.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              { id: resp.id, type: EquipmentApiTag.RIG },
              EquipmentApiTag.RIG_LIST,
            ]
          : [EquipmentApiTag.RIG_LIST],
    }),
    checkRigCompatibility: builder.query<
      CheckRigCompatibilityResponse,
      { rigId?: string; serial?: string; snapshotId: string }
    >({
      query: ({ snapshotId, rigId, serial }) => ({
        url: `atk-equipment/rig-snapshots/${snapshotId}/compatible`,
        method: "GET",
        params: {
          rigId,
          serial,
        },
      }),
      transformResponse: (response: unknown) =>
        CheckRigCompatibilityResponse.fromJSON(response),
      // this data goes stale fast; there are non-local dependencies between
      // the rig and the tractor and equipment stored inside it, so it's
      // impossible to invalidate the cache in a principled way.  it's good
      // only for one page load.
      keepUnusedDataFor: 0,
    }),
    listCompatibleRobots: builder.query<
      ListCompatibleRobotsResponse,
      { serials: string[]; snapshotId: string }
    >({
      query: (request) => ({
        url: `atk-equipment/rig-snapshots/${request.snapshotId}/compatible-robots`,
        method: "GET",
        params: {
          serials: request.serials,
        },
      }),
      transformResponse: (response: unknown) =>
        ListCompatibleRobotsResponse.fromJSON(response),
      // this data goes stale fast; there are non-local dependencies between
      // the rig and the tractor and equipment stored inside it, so it's
      // impossible to invalidate the cache in a principled way.  it's good
      // only for one page load.
      keepUnusedDataFor: 0,
    }),
  }),
  overrideExisting: "throw",
});
